"use client";
import React, { useMemo } from "react";
import { useCurrency } from "@/contexts/UserPreferencesContext/hooks/useCurrency";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import Icon from "@/components/icons";
import useIsMobile from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import CurrencyPill from "../CurrencyPill";
import type { MonarchCurrencySelectorProps } from "@/monarch/types/MonarchCurrencySelector";
import DualPriceDisplay from "@/components/DualPriceDisplay";
import type { MultiCurrencyPdpConfig } from "@/lib/currency/types";
import { formatDualPrice } from "@/utils/functions/formatDualPrice";
import { CURRENCY_BAR_LABEL_CLASS_NAME } from "../CurrencyBar/constants";
import { useCountryAutoDetectionCurrency } from "@/lib/currency/useCountryAutoDetectionCurrency";
import useTranslations from "@/i18n/useTranslations";

const INLINE_PILL_SELECTOR: MonarchCurrencySelectorProps = {
  placement: "inline-dropdown",
};

const BAR_PILL_SELECTOR: MonarchCurrencySelectorProps = {
  placement: "currency-bar",
};

type CurrencyInlinePillVariant = "default" | "inverse";

interface CurrencyInlinePillProps {
  price: number;
  originalPrice?: number;
  label?: string;
  mobileLabel?: string;
  tooltip?: string;
  location: string;
  className?: string;
  layout?: "default" | "bar" | "sideRail";
  stacked?: boolean;
  wideLabelGap?: boolean;
  useBarCurrencySelector?: boolean;
  showPrice?: boolean;
  variant?: CurrencyInlinePillVariant;
  onCurrencyChange?: (code: string) => void;
  multiCurrencyConfig?: MultiCurrencyPdpConfig;
  countryAutoDetection?: boolean;
}

export default function CurrencyInlinePill({
  price,
  originalPrice,
  label,
  mobileLabel,
  tooltip,
  location,
  className,
  layout = "default",
  stacked = false,
  wideLabelGap = false,
  useBarCurrencySelector = false,
  showPrice = true,
  variant = "default",
  onCurrencyChange,
  multiCurrencyConfig,
  countryAutoDetection,
}: CurrencyInlinePillProps) {
  const t = useTranslations("Currency");

  useCountryAutoDetectionCurrency({ countryAutoDetection });

  const { getConvertedProductPrice } = useCurrency();
  const isMobile = useIsMobile();
  const effectiveLabel = isMobile && mobileLabel ? mobileLabel : label;
  const currencySelector =
    layout === "bar" || useBarCurrencySelector
      ? BAR_PILL_SELECTOR
      : INLINE_PILL_SELECTOR;
  const isSideRailLayout = layout === "sideRail";

  const { convertedPrice, currencyCode, isLoading } = useMemo(() => {
    const result = getConvertedProductPrice(price, currencySelector);
    return {
      convertedPrice: result.convertedPrice,
      currencyCode: result.displayCurrency.code,
      isLoading: result.isLoading,
    };
  }, [price, getConvertedProductPrice, currencySelector]);

  const originalConverted = useMemo(() => {
    if (
      isLoading ||
      originalPrice == null ||
      originalPrice === price ||
      convertedPrice == null ||
      price <= 0
    ) {
      return null;
    }

    return originalPrice * (convertedPrice / price);
  }, [originalPrice, price, convertedPrice, isLoading]);

  const strikethroughDisplay = useMemo(() => {
    if (
      originalConverted == null ||
      originalPrice == null ||
      originalPrice === price
    ) {
      return null;
    }

    const formatted = formatDualPrice({
      usdAmount: originalPrice,
      displayCurrencyCode: currencyCode,
      convertedAmount: originalConverted,
      acceptedCurrencyCodes: multiCurrencyConfig?.acceptedCurrencyCodes,
      dualPricingEnabled: multiCurrencyConfig?.dualPricingEnabled,
    });

    return formatted.mode === "dual" && formatted.secondary
      ? `${formatted.primary} / ${formatted.secondary}`
      : formatted.primary;
  }, [
    originalConverted,
    originalPrice,
    price,
    multiCurrencyConfig,
    currencyCode,
  ]);

  const labelNode = effectiveLabel ? (
    <span
      className={cn(
        layout === "bar" || (isSideRailLayout && useBarCurrencySelector)
          ? cn(
              CURRENCY_BAR_LABEL_CLASS_NAME,
              !stacked && "whitespace-nowrap",
              layout === "bar" && !stacked && wideLabelGap && "mr-4",
            )
          : cn(
              "currency-inline-pill-label w-full min-w-0 md:w-auto",
              variant === "inverse" ? "text-white" : "text-gray-700",
            ),
        isSideRailLayout && "whitespace-normal",
      )}
    >
      {effectiveLabel}
    </span>
  ) : null;

  const priceNode = (
    <>
      {showPrice &&
        (isLoading ? (
          <Skeleton
            className="inline-block h-4 w-16 translate-y-0.5"
            role="status"
            aria-label={t("converting")}
          />
        ) : (
          <>
            {strikethroughDisplay != null && (
              <span
                className={cn(
                  "line-through",
                  variant === "inverse" ? "text-white/70" : "text-gray-500",
                )}
              >
                {strikethroughDisplay}{" "}
              </span>
            )}
            <DualPriceDisplay
              usdAmount={price}
              displayCurrencyCode={currencyCode}
              convertedAmount={convertedPrice ?? undefined}
              multiCurrencyConfig={multiCurrencyConfig}
              className={cn(
                "font-semibold",
                variant === "inverse" && "text-white",
              )}
            />
          </>
        ))}
      {tooltip && (
        <span className="inline-flex items-center pl-1">
          <Popover>
            <PopoverTrigger aria-label={t("information")}>
              <Icon
                type="info"
                svgProps={{
                  width: 18,
                  height: 18,
                  className:
                    variant === "inverse"
                      ? "text-white/70 hover:text-white"
                      : "text-gray-500 hover:text-gray-700",
                }}
              />
            </PopoverTrigger>
            <PopoverContent
              align="start"
              side={isMobile ? "bottom" : "right"}
              className="max-w-xs bg-primary text-primary-foreground rounded-xl text-sm"
            >
              {tooltip}
            </PopoverContent>
          </Popover>
        </span>
      )}
    </>
  );

  const pillNode = (
    <CurrencyPill location={location} onCurrencyChange={onCurrencyChange} />
  );

  if (isSideRailLayout) {
    return (
      <div
        className={cn(
          "flex w-full min-w-0 flex-col items-stretch gap-2",
          className,
        )}
      >
        {labelNode}
        <span className="min-w-0 inline-flex flex-wrap items-center gap-1 text-base">
          {priceNode}
        </span>
        <div className="self-start shrink-0">{pillNode}</div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        layout === "bar" && stacked
          ? "flex flex-col items-start gap-2"
          : layout === "bar"
            ? "flex flex-nowrap items-center gap-2"
            : "flex w-full min-w-0 max-w-full flex-col items-start gap-1 md:flex-row md:flex-nowrap md:items-center md:gap-1",
        className,
      )}
    >
      {effectiveLabel && layout === "bar" && labelNode}
      {effectiveLabel && layout !== "bar" && labelNode}

      <span
        className={cn(
          "min-w-0 inline-flex flex-wrap items-center gap-1 max-w-full w-full md:w-auto",
          layout === "bar" ? "w-max text-lg" : "text-base",
        )}
      >
        {priceNode}
      </span>

      <div
        className={layout === "bar" ? "w-auto shrink-0" : "w-full sm:w-auto"}
      >
        {pillNode}
      </div>
    </div>
  );
}
